// shared/seismic/envelope-codec.ts
import pako from "pako";
const MAX_INT16 = 32767;
/**
 * Quantize a physical amplitude value to Int16.
 * Maps [-rangeMax, +rangeMax] → [-32767, +32767].
 * Values outside the range are clamped. Never returns -32768 (sentinel).
 */
export function quantize(physicalValue, rangeMax) {
    const scaled = (physicalValue / rangeMax) * MAX_INT16;
    return Math.max(-MAX_INT16, Math.min(MAX_INT16, Math.round(scaled)));
}
/**
 * Dequantize an Int16 value back to physical units.
 */
export function dequantize(int16Value, rangeMax) {
    return (int16Value / MAX_INT16) * rangeMax;
}
/**
 * Encode min/max Int16 arrays into a gzipped binary buffer.
 * Layout: [mins (N × 2 bytes)] [maxs (N × 2 bytes)], gzipped.
 * Accepts any array length (tile size varies by level).
 */
export function encodeEnvelopeTile(mins, maxs) {
    if (mins.length !== maxs.length) {
        throw new Error(`mins and maxs must have the same length (got ${mins.length} and ${maxs.length})`);
    }
    const pointCount = mins.length;
    const raw = new ArrayBuffer(pointCount * 2 * 2);
    const view = new Int16Array(raw);
    view.set(mins, 0);
    view.set(maxs, pointCount);
    const compressed = pako.gzip(new Uint8Array(raw));
    return compressed.buffer.slice(compressed.byteOffset, compressed.byteOffset + compressed.byteLength);
}
/**
 * Decode a gzipped binary buffer back to min/max Int16 arrays.
 * Infers point count from the decompressed buffer size.
 */
export function decodeEnvelopeTile(buffer) {
    // Envelope files are stored zipped, so we try to unzip them.
    // But they might get unzipped automatically, so if the unzip fails we just use the raw data.
    let decompressed;
    try {
        decompressed = pako.ungzip(new Uint8Array(buffer));
    }
    catch (_a) {
        decompressed = new Uint8Array(buffer);
    }
    // Each point has 2 Int16 values (min + max) = 4 bytes
    const pointCount = decompressed.byteLength / 4;
    const view = new Int16Array(decompressed.buffer, decompressed.byteOffset, pointCount * 2);
    return {
        mins: view.slice(0, pointCount),
        maxs: view.slice(pointCount, pointCount * 2),
    };
}
//# sourceMappingURL=data:application/json;base64,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